/* eslint-disable no-param-reassign */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import songsApi from 'services/api/songs/songsApi';
import distributionApi from 'services/api/distribution';
import { remixerApi } from 'services/api/remixer';
import { ReleaseList } from 'services/api/entities';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import { Song } from '../songs/types';
import {
  BundleSongStatus,
  FormulaFields,
  VegaBundle,
  type UserLoop,
  BundlesPage,
} from '../ai/types';
import { namespace } from './constants';
import initialState from './initialState';

const newSongsSlice = createSlice({
  name: namespace,
  initialState,
  reducers: {
    storeSongs: (
      state,
      action: PayloadAction<{ songs: Record<string, Song> }>
    ) => {
      const {
        payload: { songs },
      } = action;

      Object.values(songs).forEach((song) => {
        state.songs[song.id] = { loading: false, error: null, data: song };
      });
    },
    storeSongLoading: (state, action: PayloadAction<{ songId: string }>) => {
      const {
        payload: { songId },
      } = action;

      state.songs[songId] = { loading: true, error: null, data: null };
    },
    storeSongSuccess: (state, action: PayloadAction<{ song: Song }>) => {
      const {
        payload: { song },
      } = action;

      state.songs[song.id] = { loading: false, error: null, data: song };
    },
    storeSongError: (
      state,
      action: PayloadAction<{ songId: string; error: Error }>
    ) => {
      const {
        payload: { songId, error },
      } = action;

      state.songs[songId] = { loading: false, error, data: null };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      distributionApi.endpoints.createRelease.matchFulfilled,
      (state, action) => {
        const { songs } = action.payload;

        Object.values(songs).forEach((song) => {
          state.songs[song.id] = { loading: false, error: null, data: song };
        });
      }
    );
    builder.addMatcher(
      (action): action is { payload: ReleaseList } =>
        distributionApi.endpoints.getAllReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getDraftReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getPendingReleases.matchFulfilled(action) ||
        distributionApi.endpoints.getReleasedReleases.matchFulfilled(action),
      (state, action) => {
        const { songs } = action.payload;

        Object.values(songs).forEach((song) => {
          state.songs[song.id] = { loading: false, error: null, data: song };
        });
      }
    );
    builder.addMatcher(
      remixerApi.endpoints.getStudioSongs.matchFulfilled,
      (state, action) => {
        const { songs } = action.payload;

        Object.values(songs).forEach((song) => {
          state.songs[song.id] = { loading: false, error: null, data: song };
        });
      }
    );
    builder.addMatcher(
      songsApi.endpoints.addToLibrary.matchFulfilled,
      (state, action) => {
        const {
          meta: {
            arg: { originalArgs },
          },
        } = action;

        const { songId } = originalArgs;
        const { data } = state.songs[songId];
        if (!data) {
          return;
        }

        data.isInLibrary = true;
      }
    );
    builder.addMatcher(
      songsApi.endpoints.removeFromLibrary.matchFulfilled,
      (state, action) => {
        const {
          meta: {
            arg: { originalArgs },
          },
        } = action;

        const { songId } = originalArgs;
        const { data } = state.songs[songId];
        if (!data) {
          return;
        }

        data.isInLibrary = false;
      }
    );
    builder.addMatcher(
      (action): action is { payload: VegaBundle | BundlesPage } =>
        aiMusicGeneratorApi.endpoints.getBundleById.matchFulfilled(action) ||
        aiMusicGeneratorApi.endpoints.getBundles.matchFulfilled(action),
      (state, action) => {
        let songs: VegaBundle['songs'] = [];

        if ('songs' in action.payload) {
          songs = action.payload.songs;
        }

        if ('items' in action.payload) {
          songs = action.payload.items.reduce<VegaBundle['songs']>(
            (acc, item) => [...acc, ...item.songs],
            []
          );
        }

        songs.forEach((songState) => {
          if (songState.status !== BundleSongStatus.SUCCESS) {
            return;
          }

          const { song } = songState;
          const currentSong = state.songs[song.id];

          if (currentSong) {
            return;
          }

          const userLoop =
            song.formula.find(
              (item): item is UserLoop => item.field === FormulaFields.UserLoop
            )?.value || null;

          state.songs[song.id] = {
            loading: false,
            error: null,
            data: {
              ...song,
              peaks: null,
              userLoop,
            },
          };
        });
      }
    );
  },
});

export const { actions, reducer } = newSongsSlice;
